import React, { useState } from "react";
import { Plus, Search } from "lucide-react";
import { Conversation } from "../types";
import {
  Sidebar,
  ConversationItem,
  Avatar,
  Badge,
  HeaderContainer,
  NewChatButton,
  FilterContainer,
  FilterTab,
  SidebarSearchWrapper,
} from "../styles";
import { formatDistanceToNow } from "date-fns";
import useConfig from "@hooks/useConfig";
import { Helper } from "@utils/index";

interface Props {
  conversations: Conversation[];
  activeId: number | null;
  onSelect: (id: number | string) => void;
  currentUser: { id: number; role: string } | null;
  onlineUsers: Record<string, boolean>;
  currentFilter: string;
  onFilterChange: (filter: string) => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  onNewChat: () => void;
}

const ConversationList: React.FC<Props> = ({
  conversations,
  activeId,
  onSelect,
  currentUser,
  onlineUsers,
  currentFilter,
  onFilterChange,
  searchTerm,
  onSearchChange,
  onNewChat,
}) => {
  const { bucketUrl } = useConfig();
  const allFilters = [
    { id: "all", label: "All" },
    { id: "store", label: "Store" },
    { id: "customer", label: "Customer" },
    { id: "delivery", label: "Delivery" },
    { id: "order", label: "Order" },
  ];

  const filters = currentUser?.role === "admin" 
    ? allFilters 
    : allFilters.filter(f => f.id === "all");

  return (
    <Sidebar>
      <HeaderContainer>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
          <h2 style={{ margin: 0 }}>Messages</h2>
          {currentUser?.role === "admin" && (
            <NewChatButton onClick={onNewChat}>
              <Plus size={18} />
            </NewChatButton>
          )}
        </div>
        <SidebarSearchWrapper>
          <Search size={16} />
          <input
            placeholder="Search people..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </SidebarSearchWrapper>
      </HeaderContainer>

      <FilterContainer>
        {filters.map((f) => (
          <FilterTab
            key={f.id}
            active={currentFilter === f.id}
            onClick={() => onFilterChange(f.id)}
          >
            {f.label}
          </FilterTab>
        ))}
      </FilterContainer>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {conversations.map((conv) => {
          // Find the "other" participant to show as the title
          const otherParticipant =
            conv.participants.find(
              (p) =>
                p.participantId !== currentUser?.id ||
                p.role !== currentUser?.role,
            ) || conv.participants[0];

          const userKey = `${otherParticipant?.participantId}-${otherParticipant?.participantType === "admin" ? "admin" : "user"}`;
          const isOnline = onlineUsers[userKey];

          return (
            <ConversationItem
              key={conv.id}
              active={activeId === conv.id}
              onClick={() => onSelect(conv.id)}
            >
              <div style={{ position: "relative" }}>
                <Avatar
                  src={
                    otherParticipant?.avatar
                      ? `${bucketUrl}/${otherParticipant.avatar}`
                      : null
                  }
                  style={{
                    backgroundColor: !otherParticipant?.avatar
                      ? Helper.getDynamicColor(
                          otherParticipant?.name || "Zedfo",
                        )
                      : undefined,
                  }}
                >
                  {!otherParticipant?.avatar &&
                    Helper.getInitials(
                      conv.type === "order"
                        ? `Order #${conv.orderId}`
                        : otherParticipant?.name || "Zedfo",
                    )}
                </Avatar>
                {isOnline && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "2px",
                      right: "2px",
                      width: "12px",
                      height: "12px",
                      background: "#44b700",
                      borderRadius: "50%",
                      border: "2px solid white",
                      boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.8)",
                    }}
                  />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "4px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {conv.type === "order"
                      ? `Order #${conv.orderId}`
                      : otherParticipant?.name || "Chat"}
                  </span>
                  {conv.unreadCount > 0 && (
                    <div
                      style={{
                        background: "#ff3135",
                        color: "white",
                        borderRadius: "10px",
                        padding: "2px 6px",
                        fontSize: "10px",
                      }}
                    >
                      {conv.unreadCount}
                    </div>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "2px",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    {conv.latestMessage?.content || "No messages yet"}
                  </span>
                  <span
                    style={{
                      fontSize: "10px",
                      color: "#999",
                      marginLeft: "10px",
                    }}
                  >
                    {conv.latestMessage?.createdAt &&
                      formatDistanceToNow(
                        new Date(conv.latestMessage.createdAt),
                        { addSuffix: true },
                      )}
                  </span>
                </div>
                <div style={{ marginTop: "5px" }}>
                  <Badge role={otherParticipant?.role}>
                    {otherParticipant?.role}
                  </Badge>
                  {conv.type === "order" && (
                    <Badge
                      role="admin"
                      style={{
                        marginLeft: "4px",
                        background: "#fff9c4",
                        color: "#fbc02d",
                      }}
                    >
                      Order
                    </Badge>
                  )}
                </div>
              </div>
            </ConversationItem>
          );
        })}
      </div>
    </Sidebar>
  );
};

export default ConversationList;
